/**
 * Error Message Sanitizer for Production Security
 * Prevents information disclosure through error messages
 */

/**
 * Sanitize error messages for production environment
 * Removes sensitive information and normalizes error messages
 */
export function sanitizeErrorMessage(error: Error | string | unknown): string {
  const isProduction = import.meta.env.PROD;
  
  let errorMessage = '';
  
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = 'An unexpected error occurred';
  }
  
  // In production, sanitize potentially sensitive information
  if (isProduction) {
    // Remove file paths
    errorMessage = errorMessage.replace(/[a-zA-Z]:\\[^\\s]+/g, '[file-path]');
    errorMessage = errorMessage.replace(/\/[^\s]+/g, '[file-path]');
    
    // Remove URLs with credentials
    errorMessage = errorMessage.replace(/https?:\/\/[^@\s]+@[^\s]+/g, '[protected-url]');
    
    // Remove JWT tokens and API keys
    errorMessage = errorMessage.replace(/[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/g, '[token]');
    errorMessage = errorMessage.replace(/[Bb]earer\s+[a-zA-Z0-9._-]+/g, '[bearer-token]');
    
    // Remove email addresses  
    errorMessage = errorMessage.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]');
    
    // Remove database connection strings
    errorMessage = errorMessage.replace(/postgresql:\/\/[^\s]+/g, '[db-connection]');
    errorMessage = errorMessage.replace(/mongodb:\/\/[^\s]+/g, '[db-connection]');
    
    // Normalize common error types to generic messages
    const sanitizedErrors: Record<string, string> = {
      'NetworkError': 'Network connection failed',
      'TypeError': 'Invalid data format',
      'ReferenceError': 'Resource not available',
      'SyntaxError': 'Data format error',
      'TimeoutError': 'Request timeout',
      'AbortError': 'Request cancelled',
      'QuotaExceededError': 'Storage limit exceeded'
    };
    
    // Check if error matches known patterns
    for (const [pattern, replacement] of Object.entries(sanitizedErrors)) {
      if (errorMessage.includes(pattern)) {
        return replacement;
      }
    }
    
    // Generic error for anything too specific in production
    if (errorMessage.length > 100) {
      return 'An error occurred while processing your request';
    }
  }
  
  return errorMessage;
}

/**
 * Create sanitized error object for logging/display
 */
export function createSanitizedError(error: Error | string | unknown, context?: string): {
  message: string;
  context?: string;
  timestamp: string;
} {
  return {
    message: sanitizeErrorMessage(error),
    context,
    timestamp: new Date().toISOString()
  };
}

/**
 * Log error safely (development details, production sanitized)
 */
export function logSecureError(error: Error | string | unknown, context?: string): void {
  const sanitized = createSanitizedError(error, context);
  
  if (import.meta.env.DEV) {
    // Development: show full error details
    console.error(`🚨 Error in ${context || 'app'}:`, error);
  } else {
    // Production: only sanitized message
    console.error(`Error: ${sanitized.message}`, sanitized);
  }
}

/**
 * Get user-friendly error message for UI display
 */
export function getUserFriendlyError(error: Error | string | unknown): string {
  const sanitized = sanitizeErrorMessage(error);
  
  // Map technical errors to user-friendly messages
  const userFriendlyMessages: Record<string, string> = {
    'Network connection failed': 'Please check your internet connection and try again.',
    'Request timeout': 'The request is taking too long. Please try again.',
    'Invalid data format': 'There was a problem with the data format.',
    'Resource not available': 'The requested resource is not available.',
    'Storage limit exceeded': 'Your storage limit has been reached.',
    'Request cancelled': 'The request was cancelled.',
  };
  
  return userFriendlyMessages[sanitized] || 'Something went wrong. Please try again.';
}
